import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { userRoleSchema } from '@threadheads/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { secretAutofillProps } from '@/components/ui/no-autofill';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { Copy, RefreshCw, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useUserManagementLocations } from '../hooks/useUsers';
import { useUserManagement } from '../hooks/useUserManagement';
import { trpc } from '@/lib/trpc';
import ViewerPagePicker from './ViewerPagePicker';
import { DEFAULT_VIEWER_PAGES } from '@/lib/page-registry';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { CLIENT_MODE_ROLE, CLIENT_ROLE_LABEL } from '../utils/role-style';
import { DTF_FLOOR_HINT, dtfFloorPick, pickerRoles } from '../utils/assignable-roles';
import type { UserRole } from '../types';

interface AddUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Forces client mode when true; omitted follows the server's pure-client verdict.
   * The onboarding invite step passes true for a client org.
   */
  clientMode?: boolean;
}

type Step = 'email' | 'existing' | 'new';

export default function AddUserModal({ open, onOpenChange, clientMode }: AddUserModalProps) {
  // Must match backend regex
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  // React Hook Form schema for new-user step
  const newUserSchema = z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
  });
  const newUserForm = useForm<{ firstName: string; lastName: string; password: string }>({
    resolver: zodResolver(newUserSchema),
    mode: 'onChange',
    defaultValues: { firstName: '', lastName: '', password: '' },
  });
  // Existing user step form (role + locations)
  const existingUserSchema = z.object({
    // Shared enum, not a hand-typed list — a local list here omitted 'CX'
    // and silently failed validation for it.
    role: userRoleSchema,
    locationIds: z.array(z.string()).default([]),
  });
  const existingForm = useForm<{ role: UserRole; locationIds: string[] }>({
    resolver: zodResolver(existingUserSchema),
    mode: 'onChange',
    defaultValues: { role: 'Warehouse Staff', locationIds: [] },
  });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('Warehouse Staff');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [viewerVisiblePages, setViewerVisiblePages] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const { profile: _profile } = useAuth();

  // Client mode: a pure-client 3PL org has one login type, so both the role and
  // location pickers go away and the role is pinned at the mutation. See
  // utils/role-style.ts, and note the location-required submit gates below must
  // relax too or a client could never add anyone.
  const { isPureClient, hidesProduction } = useOrgRoleSurfaces();
  const isClientMode = clientMode ?? isPureClient;
  const { organization } = useOrganization();
  const roleOptions = pickerRoles({ runsOwnFloor: !hidesProduction });

  const existingFormRole = existingForm.watch('role');
  const isViewerSelected = !isClientMode && (role === 'Viewer' || existingFormRole === 'Viewer');

  // Seed picker with default pages on first switch to Viewer.
  useEffect(() => {
    if (isViewerSelected && viewerVisiblePages.length === 0) {
      setViewerVisiblePages([...DEFAULT_VIEWER_PAGES]);
    }
  }, [isViewerSelected, viewerVisiblePages.length]);

  // Use hooks for data and operations
  const { locations } = useUserManagementLocations();
  // In client mode there is no location picker to satisfy, so the submit gates
  // below must not demand a selection — a client org owns its delegated shadow
  // location, so the `locations.length > 0` escape hatch would never fire.
  const locationsRequired = !isClientMode && (locations?.length ?? 0) > 0;
  // A DTF Printer is offered, and submits, only floors the org owns; the submit gate
  // counts those, not every box ticked before the role was switched.
  const existingChosen = existingForm.watch('locationIds');
  const {
    floors: existingFloors,
    submittedIds: existingSubmitted,
    isDtf: isExistingDtf,
  } = dtfFloorPick(locations ?? [], existingFormRole, organization?.id, existingChosen);
  const {
    floors: newFloors,
    submittedIds: newSubmitted,
    isDtf: isNewDtf,
  } = dtfFloorPick(locations ?? [], role, organization?.id, selectedLocations);
  const existingNeedsFloor = isExistingDtf ? existingSubmitted.length === 0 : existingChosen.length === 0 && locationsRequired;
  const newNeedsFloor = isNewDtf ? newSubmitted.length === 0 : selectedLocations.length === 0 && locationsRequired;
  const { createUser, addMembershipByEmail } = useUserManagement();
  const checkEmail = trpc.users.checkEmailExists.useQuery({ email }, { enabled: false });
  const isEmailValid = useMemo(() => {
    const v = email.trim().toLowerCase();
    // simple email validation for enabling the button
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, [email]);

  const generatePassword = () => {
    const length = 12;
    const lowers = 'abcdefghijklmnopqrstuvwxyz';
    const uppers = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const specials = '@$!%*?&'; // only the allowed special characters
    const all = lowers + uppers + digits + specials;

    const rand = (max: number) => {
      const cryptoObj: Crypto | undefined =
        typeof globalThis !== 'undefined' && (globalThis as Record<string, unknown>).crypto
          ? ((globalThis as unknown as { crypto?: Crypto }).crypto)
          : undefined;
      if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
        const arr = new Uint32Array(1);
        cryptoObj.getRandomValues(arr);
        const n = arr[0] ?? 0;
        return n % max;
      }
      return Math.floor(Math.random() * max);
    };

    const pick = (set: string) => set[rand(set.length)];

    // Ensure at least one of each required category
    const chars = [pick(lowers), pick(uppers), pick(digits), pick(specials)];
    for (let i = chars.length; i < length; i++) {
      chars.push(pick(all));
    }

    // Fisher–Yates shuffle using crypto-safe randomness when available
    for (let i = chars.length - 1; i > 0; i--) {
      const j = rand(i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }

    const newPassword = chars.join('');
    // Sanity check: should always pass, but validate to be safe
    if (!passwordRegex.test(newPassword)) {
      // Rare edge case fallback: regenerate recursively
      return generatePassword();
    }
    newUserForm.setValue('password', newPassword, { shouldValidate: true });
    setShowPassword(true);
  };

  const copyPassword = () => {
    const pwd = newUserForm.getValues('password');
    if (!pwd) return;
    navigator.clipboard.writeText(pwd);
    toast({
      title: 'Copied',
      description: 'Password copied to clipboard',
    });
  };


  const [step, setStep] = useState<Step>('email');

  const resetForm = () => {
    newUserForm.reset({ firstName: '', lastName: '', password: '' });
    setEmail('');
    setRole('Warehouse Staff');
    setSelectedLocations([]);
    setViewerVisiblePages([]);
    setShowPassword(false);
    setStep('email');
  };

  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await checkEmail.refetch();
    const exists = !!res.data?.exists;
    setStep(exists ? 'existing' : 'new');
  };

  const handleAddExisting = (e: React.FormEvent) => {
    e.preventDefault();
    const { role: exRole } = existingForm.getValues();
    // Pin at the mutation, not just by hiding the picker: the form's defaultValues
    // are captured before the org-shape verdict resolves, so `exRole` is still
    // Warehouse Staff — which with no locations is a user who can never sign in.
    const effectiveRole = isClientMode ? CLIENT_MODE_ROLE : exRole;
    addMembershipByEmail.mutate(
      {
        email,
        role: effectiveRole,
        locationIds: !isClientMode && existingSubmitted.length ? existingSubmitted : undefined,
        ...(effectiveRole === 'Viewer' ? { viewerVisiblePages } : {}),
      },
      { onSuccess: () => { resetForm(); onOpenChange(false); } }
    );
  };

  const handleCreateNew = (e: React.FormEvent) => {
    e.preventDefault();
    const { firstName, lastName, password } = newUserForm.getValues();
    createUser.mutate(
      {
        email,
        password,
        firstName,
        lastName,
        role: isClientMode ? CLIENT_MODE_ROLE : role,
        locationIds: !isClientMode && newSubmitted.length ? newSubmitted : undefined,
        ...(!isClientMode && role === 'Viewer' ? { viewerVisiblePages } : {}),
      },
      { onSuccess: () => { resetForm(); onOpenChange(false); } }
    );
  };

  const toggleLocation = (locationId: string) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!createUser.isPending && !addMembershipByEmail.isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[525px] max-h-[90dvh] flex flex-col">
        {step === 'email' && (
          <form onSubmit={handleCheckEmail} className="flex flex-col min-h-0 flex-1">
            <DialogHeader>
              <DialogTitle>Add User</DialogTitle>
              <DialogDescription>
                Enter the user’s email. If the user exists, we’ll add them to this store. Otherwise, you’ll create a new account.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                <p className="text-xs text-muted-foreground">If this email already exists, we’ll add them to this store and ignore the password.</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={!isEmailValid || checkEmail.isFetching}>
                {checkEmail.isFetching ? 'Checking…' : 'Continue'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 'existing' && (
          <form onSubmit={handleAddExisting} className="flex flex-col min-h-0 flex-1">
            <DialogHeader>
              <DialogTitle>Add Existing User</DialogTitle>
              <DialogDescription>
                {isClientMode
                  ? `We found an account for ${email}. Add them to this organization.`
                  : `We found an account for ${email}. Set role and locations to add them to this store.`}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={email} disabled />
              </div>
              {!isClientMode && (
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={existingForm.watch('role')} onValueChange={(v) => existingForm.setValue('role', v as UserRole, { shouldValidate: true })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* "Super Admin" is a Blue Monk developer role, granted out of band
                        via service-role script — the API refuses to assign it. */}
                    {roleOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              {!isClientMode && (
              <div className="space-y-2">
                <Label>Location Access</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-3">
                  {existingFloors.map((location) => {
                    const chosen = existingForm.watch('locationIds');
                    const checked = chosen.includes(location.id);
                    const toggle = () => {
                      const next = checked ? chosen.filter(id => id !== location.id) : [...chosen, location.id];
                      existingForm.setValue('locationIds', next, { shouldValidate: true });
                    };
                    return (
                      <div key={location.id} className="flex items-center space-x-2">
                        <Checkbox id={location.id} checked={checked} onCheckedChange={toggle} />
                        <Label htmlFor={location.id} className="text-sm font-medium leading-none">{location.name}</Label>
                      </div>
                    );
                  })}
                </div>
                {isExistingDtf && (
                  <p className="text-xs text-muted-foreground">
                    {DTF_FLOOR_HINT}
                  </p>
                )}
                {existingNeedsFloor && (
                  <p className="text-xs text-destructive">
                    Select at least one location. A user with no location access can’t sign in.
                  </p>
                )}
              </div>
              )}
              {isClientMode && (
                <p className="text-sm text-muted-foreground">
                  They will be added as {CLIENT_ROLE_LABEL}, with full access to this organization.
                </p>
              )}
              {!isClientMode && existingFormRole === 'Viewer' && (
                <ViewerPagePicker value={viewerVisiblePages} onChange={setViewerVisiblePages} />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep('email')} disabled={addMembershipByEmail.isPending}>Back</Button>
              <Button
                type="submit"
                disabled={addMembershipByEmail.isPending || existingNeedsFloor}
              >
                Add to Store
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 'new' && (
          <form onSubmit={handleCreateNew} className="flex flex-col min-h-0 flex-1" autoComplete="off">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>Create a new user account. The user will receive their login credentials separately.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input id="firstName" {...newUserForm.register('firstName')} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input id="lastName" {...newUserForm.register('lastName')} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} disabled />
              </div>
              {!isClientMode && (
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="flex gap-2">
                  <Input type={showPassword ? 'text' : 'password'} {...newUserForm.register('password')} readOnly placeholder="Click generate to create password" {...secretAutofillProps} />
                  <Button type="button" variant="outline" onClick={generatePassword}><RefreshCw className="h-4 w-4" /></Button>
                  {newUserForm.watch('password') && (
                    <Button type="button" variant="outline" onClick={copyPassword}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {newUserForm.watch('password') && showPassword && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Save this password! It will only be shown once.</p>
                    <div className="flex items-center gap-1 text-xs text-green-600"><Check className="h-3 w-3" /><span>{passwordRegex.test(newUserForm.watch('password') || '') ? 'Meets requirements: uppercase, lowercase, number, special' : 'Password does not meet requirements'}</span></div>
                  </div>
                )}
              </div>
              {!isClientMode && (
              <div className="space-y-2">
                <Label>Location Access</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-3">
                  {newFloors.map((location) => (
                    <div key={location.id} className="flex items-center space-x-2">
                      <Checkbox id={location.id} checked={selectedLocations.includes(location.id)} onCheckedChange={() => toggleLocation(location.id)} />
                      <Label htmlFor={location.id} className="text-sm font-medium leading-none">{location.name}</Label>
                    </div>
                  ))}
                </div>
                {isNewDtf && (
                  <p className="text-xs text-muted-foreground">
                    {DTF_FLOOR_HINT}
                  </p>
                )}
                {newNeedsFloor && (
                  <p className="text-xs text-destructive">
                    Select at least one location. A user with no location access can’t sign in.
                  </p>
                )}
              </div>
              )}
              {isClientMode && (
                <p className="text-sm text-muted-foreground">
                  They will be created as {CLIENT_ROLE_LABEL}, with full access to this organization.
                </p>
              )}
              {!isClientMode && role === 'Viewer' && (
                <ViewerPagePicker value={viewerVisiblePages} onChange={setViewerVisiblePages} />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep('email')} disabled={createUser.isPending}>Back</Button>
              <Button
                type="submit"
                disabled={createUser.isPending || newNeedsFloor}
              >
                {createUser.isPending ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
